import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/api';
import livekitService from '../services/livekitService';
import { DEFAULT_VALUES } from '../constants';
import { Track } from 'livekit-client';
import i18n from 'i18next';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [authData, setAuthData] = useState({
    isAuthenticated: false,
    userId: null,
    email: null,
    firstName: null,
    lastName: null,
    accessToken: null,
    sessionId: null,
    roomName: null,
    livekitToken: null,
    livekitUrl: null,
    // Keep legacy fields for backward compatibility
    userIdentity: null,
    appId: null,
    apiKey: null,
    expiresIn: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [participants, setParticipants] = useState([]);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeError, setResumeError] = useState(null);
  const [showJDModal, setShowJDModal] = useState(false);
  const [jdError, setJDError] = useState(null);
  const [pendingLoginData, setPendingLoginData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ step: 0, total: 0 }); // Track upload sequence
  const [currentInterviewId, setCurrentInterviewId] = useState(null); // Track current interview session

  // --- THIS IS THE FIRST NEW FUNCTION ---
  const handleTrackSubscribed = (track, publication, participant) => {
    // We only care about the AI's audio track (which is remote)
    if (track.kind === Track.Kind.Audio && !participant.isLocal) {
      console.log(`🔊 AI voice connected from ${participant.identity}. Attaching audio element...`);
      // This creates and manages the <audio> element for you.
      // It will be attached to the document body where the browser can play it.
      track.attach();
      console.log('✅ Audio track attached. You should now hear the AI.');
    }
  };

  // --- THIS IS THE SECOND NEW FUNCTION ---
  const handleTrackUnsubscribed = (track) => {
    // This correctly removes the audio element when the track is gone.
    track.detach().forEach((element) => element.remove());
  };

  // Login function
  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      // Validate input
      if (!email || email.trim() === '') throw new Error('Email is required');
      if (!password || password.trim() === '') throw new Error('Password is required');

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) throw new Error('Invalid email format');

      // Authenticate with unified-backend only (no voice bot initialization yet)
      const authResponse = await apiService.login(email, password);
      console.log('✅ Unified-backend authentication successful:', authResponse.message);

      // Update auth data with unified-backend credentials
      const newAuthData = {
        isAuthenticated: true,
        userId: authResponse.user.id,
        email: authResponse.user.email,
        firstName: authResponse.user.firstName,
        lastName: authResponse.user.lastName,
        accessToken: authResponse.accessToken,
        sessionId: authResponse.sessionId,
        // LiveKit fields will be set after interview setup
        roomName: null,
        livekitToken: null,
        livekitUrl: null,
        // Keep legacy compatibility
        userIdentity: authResponse.user.email,
        apiKey: DEFAULT_VALUES.API_KEY,
        appId: DEFAULT_VALUES.APP_ID,
        expiresIn: 0
      };

      setAuthData(newAuthData);

      // Save to localStorage (save full auth data except LiveKit token)
      const dataToSave = {
        isAuthenticated: newAuthData.isAuthenticated,
        userId: newAuthData.userId,
        email: newAuthData.email,
        firstName: newAuthData.firstName,
        lastName: newAuthData.lastName,
        accessToken: newAuthData.accessToken,
        sessionId: newAuthData.sessionId,
        userIdentity: newAuthData.userIdentity,
        apiKey: newAuthData.apiKey,
        appId: newAuthData.appId
      };
      localStorage.setItem('authData', JSON.stringify(dataToSave));

      console.log('✅ Authentication successful - ready for interview setup');

      return authResponse;

    } catch (error) {
      // Enhanced error handling with specific scenarios
      const errorMessage = error.message.toLowerCase();

      // Check for resume-related errors
      if (errorMessage.includes('resume')) {
        // Check for specific resume processing status
        if (errorMessage.includes('still being processed') || errorMessage.includes('processing')) {
          setResumeError('Your resume is still being processed. Please wait a moment and try again.');
        } else {
          // Enhance error message with helpful context
          setResumeError(`${error.message}\n\nStep 1 of 2: Upload your resume first, then we'll ask for the job description.`);
        }

        setShowResumeModal(true);
        setError(null);
        setPendingLoginData({ email, password });
        setUploadProgress({ step: 1, total: 2 });
        console.log('📄 Resume issue detected - showing upload modal (Step 1 of 2)');
        return null;
      }
      // Check for job description-related errors
      else if (errorMessage.includes('job description') || errorMessage.includes('jd')) {
        // Check for specific JD processing status
        if (errorMessage.includes('still being processed') || errorMessage.includes('processing')) {
          setJDError('Your job description is still being processed. Please wait a moment and try again.');
        } else {
          // Enhance error message with helpful context
          setJDError(`${error.message}\n\nStep 2 of 2: Now upload the job description for the position you're interviewing for.`);
        }

        setShowJDModal(true);
        setError(null);
        setPendingLoginData({ email, password });
        setUploadProgress({ step: 2, total: 2 });
        console.log('📋 Job description issue detected - showing upload modal (Step 2 of 2)');
        return null;
      }
      // Check for authentication errors
      else if (errorMessage.includes('invalid') || errorMessage.includes('unauthorized') || errorMessage.includes('incorrect')) {
        setError('Invalid email or password. Please check your credentials and try again.');
        console.error('❌ Authentication failed - Invalid credentials');
        throw error;
      }
      // Check for network/connection errors
      else if (errorMessage.includes('fetch') || errorMessage.includes('network') || errorMessage.includes('connection')) {
        setError('Connection failed. Please check your internet connection and try again.');
        console.error('❌ Network error:', error);
        throw error;
      }
      // Generic errors
      else {
        setError(error.message || 'Login failed. Please try again.');
        console.error('❌ Authentication failed:', error);
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  // LiveKit Voice Connection Functions
  const connectToVoiceWithData = async (livekitUrl, livekitToken, roomName) => {
    try {
      console.log('🔗 AuthContext: Connecting to voice with data...', { livekitUrl, roomName, hasToken: !!livekitToken });

      // Set up LiveKit event handlers
      livekitService.setOnConnectionStateChange((state) => {
        console.log('🔄 AuthContext: Connection state changed to:', state);
        setConnectionState(state);
        setVoiceConnected(state === 'connected');
      });

      livekitService.setOnParticipantConnected((participant) => {
        console.log('👤 AuthContext: Participant connected:', participant.identity);
        setParticipants(prev => [...prev, participant]);
      });

      livekitService.setOnParticipantDisconnected((participant) => {
        console.log('👤 AuthContext: Participant disconnected:', participant.identity);
        setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
      });

      // --- ADD THESE LINES ---
      livekitService.setOnTrackSubscribed(handleTrackSubscribed);
      livekitService.setOnTrackUnsubscribed(handleTrackUnsubscribed);

      await livekitService.connect(livekitUrl, livekitToken, roomName);

      setVoiceConnected(true);
      console.log('✅ AuthContext: Voice connected successfully');
      console.log('🔇 AuthContext: Mic will remain muted until user unmutes');
      
    } catch (error) {
      console.error('❌ AuthContext: Voice connection failed:', error);
      setVoiceConnected(false);
      throw error;
    }
  };

  const connectToVoice = async () => {
    try {
      console.log('🔗 AuthContext: Connecting to voice...', { livekitUrl: authData.livekitUrl, roomName: authData.roomName, hasToken: !!authData.livekitToken });

      // Set up LiveKit event handlers
      livekitService.setOnConnectionStateChange((state) => {
        console.log('🔄 AuthContext: Connection state changed to:', state);
        setConnectionState(state);
        setVoiceConnected(state === 'connected');
      });

      livekitService.setOnParticipantConnected((participant) => {
        console.log('👤 AuthContext: Participant connected:', participant.identity);
        setParticipants(prev => [...prev, participant]);
      });

      livekitService.setOnParticipantDisconnected((participant) => {
        console.log('👤 AuthContext: Participant disconnected:', participant.identity);
        setParticipants(prev => prev.filter(p => p.identity !== participant.identity));
      });

      // --- ADD THESE LINES HERE AS WELL ---
      livekitService.setOnTrackSubscribed(handleTrackSubscribed);
      livekitService.setOnTrackUnsubscribed(handleTrackUnsubscribed);

      await livekitService.connect(authData.livekitUrl, authData.livekitToken, authData.roomName);

      setVoiceConnected(true);
      console.log('✅ AuthContext: Voice connected successfully');
      console.log('🔇 AuthContext: Mic will remain muted until user unmutes');
      
    } catch (error) {
      console.error('❌ AuthContext: Voice connection failed:', error);
      setVoiceConnected(false);
      throw error;
    }
  };

  // ... (The rest of the file: disconnectFromVoice, enableMicrophone, disableMicrophone, logout, etc. remains unchanged) ...
  const disconnectFromVoice = async () => {
    try {
      console.log('🔗 AuthContext: Disconnecting from voice...');
      await livekitService.disconnect();
      setVoiceConnected(false);
      setParticipants([]);

      // Clear current interview when disconnecting (interview ended)
      console.log('🔚 Clearing currentInterviewId on disconnect');
      setCurrentInterviewId(null);
      localStorage.removeItem('currentInterviewId');

      // Clear URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('interviewId');
      window.history.replaceState({}, '', url.toString());

      console.log('✅ AuthContext: Voice disconnected successfully');
    } catch (error) {
      console.error('❌ AuthContext: Voice disconnect failed:', error);
      throw error;
    }
  };

  const enableMicrophone = async () => {
    try {
      console.log('🎤 AuthContext: Enabling microphone...');
      await livekitService.enableMicrophone();
      console.log('✅ AuthContext: Microphone enabled');
    } catch (error) {
      console.error('❌ AuthContext: Microphone enable failed:', error);
      throw error;
    }
  };

  const disableMicrophone = async () => {
    try {
      console.log('🎤 AuthContext: Disabling microphone...');
      await livekitService.disableMicrophone();
      console.log('✅ AuthContext: Microphone disabled');
    } catch (error) {
      console.error('❌ AuthContext: Microphone disable failed:', error);
      throw error;
    }
  };

  const startInterviewWithSelection = async (jobId, applicationId, interviewRoundIndex, resumable = false) => {
    try {
      console.log(`🎬 Starting interview with Job: ${jobId}, Application: ${applicationId}, Round: ${interviewRoundIndex}, Resumable: ${resumable}`);
      setLoading(true);
      setError(null);

      const currentLanguage = i18n.language || 'en-US';

      // Step 1: Create the interview in the sr-unified backend first
      console.log('📝 Creating interview in sr-unified backend...');
      const interviewResponse = await apiService.createInterview(
        jobId,
        applicationId,
        interviewRoundIndex,
        authData.accessToken,
        authData.sessionId,
        {
          language: currentLanguage,
          resumable: resumable
        }
      );

      // Validate interview creation response
      // Handle nested response structure: { status, message, data: { sessionId, ... } }
      const interviewData = interviewResponse.data || interviewResponse;
      if (!interviewData || !interviewData.sessionId) {
        console.error('❌ Invalid interview creation response:', interviewResponse);
        throw new Error('Failed to create interview: Invalid response from server');
      }

      const interviewId = interviewData.sessionId;
      console.log(`✅ Interview created successfully with ID: ${interviewId}`);

      // Save current interview ID for resume functionality
      setCurrentInterviewId(interviewId);
      localStorage.setItem('currentInterviewId', interviewId);

      // Update URL with session ID immediately (using replaceState to avoid adding to history)
      const url = new URL(window.location.href);
      url.searchParams.set('interviewId', interviewId);
      window.history.replaceState({}, '', url.toString());
      console.log('✅ URL updated with interview ID:', interviewId);
      console.log('✅ New URL:', window.location.href);
      console.log('✅ URL search params:', window.location.search);

      // Step 2: Initialize voice bot with the interview ID
      // Voice is now handled by backend using INTERVIEW_VOICE_ID env variable
      console.log('🎙️ Initializing voice bot with interview ID...');
      console.log('📋 Interview ID:', interviewId);
      console.log('📋 Language:', currentLanguage);
      console.log('📋 Voice: Using default from INTERVIEW_VOICE_ID env variable');

      const voiceResponse = await apiService.initializeVoiceBot(
        interviewId,
        currentLanguage
      );

      console.log('✅ Voice bot initialized successfully');
      console.log('📦 Voice response:', voiceResponse);

      // Update auth data with LiveKit credentials
      const updatedAuthData = {
        ...authData,
        roomName: voiceResponse.roomName,
        livekitToken: voiceResponse.livekitToken,
        livekitUrl: voiceResponse.livekitUrl,
        expiresIn: voiceResponse.expiresIn
      };

      setAuthData(updatedAuthData);

      // Persist updated auth data to localStorage (excluding LiveKit credentials)
      const dataToSave = {
        isAuthenticated: updatedAuthData.isAuthenticated,
        userId: updatedAuthData.userId,
        email: updatedAuthData.email,
        firstName: updatedAuthData.firstName,
        lastName: updatedAuthData.lastName,
        accessToken: updatedAuthData.accessToken,
        sessionId: updatedAuthData.sessionId,
        userIdentity: updatedAuthData.userIdentity,
        apiKey: updatedAuthData.apiKey,
        appId: updatedAuthData.appId
      };
      localStorage.setItem('authData', JSON.stringify(dataToSave));

      // Auto-connect to LiveKit
      if (voiceResponse.livekitUrl && voiceResponse.livekitToken && voiceResponse.roomName) {
        console.log('🔗 AuthContext: Connecting to LiveKit voice...');
        try {
          await connectToVoiceWithData(
            voiceResponse.livekitUrl,
            voiceResponse.livekitToken,
            voiceResponse.roomName
          );
          console.log('✅ Connected to voice successfully');
        } catch (voiceError) {
          console.error('❌ Voice connection failed:', voiceError);
          throw new Error('Failed to connect to voice: ' + voiceError.message);
        }
      }

      return voiceResponse;
    } catch (error) {
      console.error('❌ Failed to start interview:', error);
      setError(error.message || 'Failed to start interview');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const resumeInterview = async (interviewSessionId, loadConversationCallback) => {
    try {
      console.log(`🔄 Resuming interview with ID: ${interviewSessionId}`);
      setLoading(true);
      setError(null);

      const currentLanguage = i18n.language || 'en-US';

      // Create a timeout promise (30 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Resume timeout - operation took too long')), 30000);
      });

      // Wrap the entire resume operation with timeout
      const resumeOperation = async () => {
        // Step 1: Get interview details to check if it's resumable and active
        const interviewDetails = await apiService.getInterview(
        interviewSessionId,
        authData.accessToken,
        authData.sessionId
      );

      const interview = interviewDetails.data || interviewDetails;

      if (!interview.resumable) {
        throw new Error('This interview is not resumable');
      }

      if (interview.status !== 'active') {
        throw new Error('Interview is not active');
      }

      console.log('✅ Interview is resumable and active, resuming...');
      console.log('📚 Found', interview.conversation?.length || 0, 'messages in history');

      // Load conversation history into chat if callback provided
      if (loadConversationCallback && interview.conversation && interview.conversation.length > 0) {
        console.log('📥 Loading conversation history into chat...');
        loadConversationCallback(interview.conversation);
      }

      // Step 2: Update elapsed time before resuming
      await apiService.updateElapsedTime(
        interviewSessionId,
        authData.accessToken,
        authData.sessionId
      );

      // Step 3: Initialize voice bot with the interview ID
      console.log('🎙️ Re-initializing voice bot...');
      const voiceResponse = await apiService.initializeVoiceBot(
        interviewSessionId,
        currentLanguage
      );

      console.log('✅ Voice bot re-initialized successfully');

      // Update auth data with LiveKit credentials
      const updatedAuthData = {
        ...authData,
        roomName: voiceResponse.roomName,
        livekitToken: voiceResponse.livekitToken,
        livekitUrl: voiceResponse.livekitUrl,
        expiresIn: voiceResponse.expiresIn
      };

      setAuthData(updatedAuthData);

      // Persist updated auth data to localStorage (excluding LiveKit credentials)
      const dataToSave = {
        isAuthenticated: updatedAuthData.isAuthenticated,
        userId: updatedAuthData.userId,
        email: updatedAuthData.email,
        firstName: updatedAuthData.firstName,
        lastName: updatedAuthData.lastName,
        accessToken: updatedAuthData.accessToken,
        sessionId: updatedAuthData.sessionId,
        userIdentity: updatedAuthData.userIdentity,
        apiKey: updatedAuthData.apiKey,
        appId: updatedAuthData.appId
      };
      localStorage.setItem('authData', JSON.stringify(dataToSave));

      // Auto-connect to LiveKit
      if (voiceResponse.livekitUrl && voiceResponse.livekitToken && voiceResponse.roomName) {
        console.log('🔗 AuthContext: Reconnecting to LiveKit voice...');
        try {
          await connectToVoiceWithData(
            voiceResponse.livekitUrl,
            voiceResponse.livekitToken,
            voiceResponse.roomName
          );
          console.log('✅ Reconnected to voice successfully');
        } catch (voiceError) {
          console.error('❌ Voice reconnection failed:', voiceError);
          throw new Error('Failed to reconnect to voice: ' + voiceError.message);
        }
      }

        return voiceResponse;
      };

      // Race between the resume operation and timeout
      return await Promise.race([resumeOperation(), timeoutPromise]);

    } catch (error) {
      console.error('❌ Failed to resume interview:', error);
      setError(error.message || 'Failed to resume interview');

      // Clear interview state on failure to prevent stuck screen
      setCurrentInterviewId(null);
      localStorage.removeItem('currentInterviewId');

      // Clear URL parameter
      const url = new URL(window.location.href);
      url.searchParams.delete('interviewId');
      window.history.replaceState({}, '', url.toString());

      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    console.log('🔐 AuthContext: Logging out...');
    if (voiceConnected) {
      try {
        await disconnectFromVoice();
      } catch (error) {
        console.warn('⚠️ AuthContext: Error disconnecting voice during logout:', error);
      }
    }

    // Clear current interview
    console.log('🔚 Clearing currentInterviewId on logout');
    setCurrentInterviewId(null);
    localStorage.removeItem('currentInterviewId');

    setAuthData({
      isAuthenticated: false,
      userId: null,
      email: null,
      firstName: null,
      lastName: null,
      accessToken: null,
      sessionId: null,
      roomName: null,
      livekitToken: null,
      livekitUrl: null,
      userIdentity: null,
      appId: null,
      apiKey: null,
      expiresIn: 0
    });
    localStorage.removeItem('authData');

    // Clear URL parameter
    const url = new URL(window.location.href);
    url.searchParams.delete('interviewId');
    window.history.replaceState({}, '', url.toString());
    setError(null);
    setVoiceConnected(false);
    setParticipants([]);
    console.log('🔓 Logged out');
  };

  const checkAuthStatus = async () => {
    // Check if user has valid authentication
    if (!authData.accessToken && !authData.apiKey) return false;

    try {
      // For new JWT auth, we rely on token expiration (handled by backend)
      // For legacy apiKey auth, check status
      if (authData.apiKey) {
        const status = await apiService.getAuthStatus(authData.apiKey);
        if (status.is_active) {
          setAuthData(prev => ({ ...prev, expiresIn: status.expires_in }));
          return true;
        } else {
          logout();
          return false;
        }
      }

      // For JWT auth, assume valid unless backend returns 401
      return true;
    } catch (error) {
      console.error('❌ Auth status check failed:', error);
      // Only logout if it's an auth error (401/403)
      if (error.message.includes('401') || error.message.includes('403') || error.message.includes('unauthorized')) {
        logout();
      }
      return false;
    }
  };

  // Resume management functions
  const uploadResume = async (file) => {
    try {
      // Validate file before upload
      if (!file) {
        throw new Error('Please select a resume file');
      }
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are supported for resumes');
      }
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Resume file size must be less than 10MB');
      }

      // Use pending login data if available, otherwise use auth data
      const loginData = pendingLoginData || authData;
      const response = await apiService.uploadResume(loginData.userIdentity, file, loginData.apiKey);
      console.log('✅ Resume uploaded successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ Resume upload failed:', error);
      // Enhance error message for common issues
      if (error.message.includes('PDF files are supported')) {
        throw new Error('Only PDF files are supported. Please convert your resume to PDF format.');
      } else if (error.message.includes('File size')) {
        throw new Error('Resume file is too large. Please reduce the file size to under 10MB.');
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        throw new Error('Upload failed due to connection issues. Please check your internet and try again.');
      }
      throw error;
    }
  };

  const handleResumeUploadSuccess = async (uploadResult) => {
    console.log('📄 Resume upload successful, retrying login...', uploadResult);
    setShowResumeModal(false);
    setResumeError(null);

    // Retry login after successful upload using pending login data
    const loginData = pendingLoginData || authData;

    try {
      setLoading(true);

      // Check if we have email/password (new auth) or old auth
      let response;
      if (loginData.email && loginData.password) {
        response = await apiService.login(loginData.email, loginData.password);

        // Update auth data with successful login
        setAuthData({
          isAuthenticated: true,
          userId: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          accessToken: response.accessToken,
          sessionId: response.sessionId,
          roomName: response.roomName || null,
          livekitToken: response.livekitToken || null,
          livekitUrl: response.livekitUrl || null,
          userIdentity: response.user.email,
          expiresIn: response.expiresIn || 0
        });

        localStorage.setItem('authData', JSON.stringify({
          userId: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          accessToken: response.accessToken,
          sessionId: response.sessionId
        }));
      } else {
        // Legacy auth support
        response = await apiService.login(loginData.userIdentity, loginData.appId, loginData.apiKey);

        setAuthData({
          isAuthenticated: true,
          sessionId: response.session_id,
          roomName: response.room_name,
          livekitToken: response.livekit_token,
          livekitUrl: response.livekit_url,
          userIdentity: response.user_identity,
          appId: loginData.appId,
          apiKey: loginData.apiKey,
          expiresIn: response.expires_in
        });
      }

      console.log('✅ Authentication successful after resume upload');

      // Reset upload progress on successful login
      setUploadProgress({ step: 0, total: 0 });

      // Auto-connect to voice if credentials are provided
      if (response.livekitUrl && response.livekitToken && response.roomName) {
        console.log('🔗 AuthContext: Auto-connecting to voice after resume upload...');
        try {
          await connectToVoiceWithData(response.livekitUrl, response.livekitToken, response.roomName);
        } catch (voiceError) {
          console.warn('⚠️ AuthContext: Auto voice connection failed:', voiceError);
        }
      }

    } catch (error) {
      // Intelligent flow: Check if the error is now about missing JD
      const errorMessage = error.message.toLowerCase();
      if (errorMessage.includes('job description') || errorMessage.includes('jd')) {
        console.log('🔄 Resume uploaded successfully, but JD is missing. Showing JD modal...');

        // Update authData to preserve user credentials for JD upload
        setAuthData(prev => ({
          ...prev,
          email: loginData.email,
          userIdentity: loginData.email || loginData.userIdentity
        }));

        setJDError(`${error.message}\n\n✅ Resume uploaded successfully!\nStep 2 of 2: Now upload the job description for the position you're interviewing for.`);
        setShowJDModal(true);
        setError(null);
        setUploadProgress({ step: 2, total: 2 });
        // Keep pending login data for JD upload
        return;
      } else {
        console.error('❌ Login retry failed after resume upload:', error);
        setError('Login failed after resume upload. Please try again.');
        setPendingLoginData(null);
      }
    } finally {
      setLoading(false);
      // Only clear pending data if login was successful or there's no JD requirement
      if (!showJDModal) {
        setPendingLoginData(null);
      }
    }
  };

  const closeResumeModal = () => {
    setShowResumeModal(false);
    setResumeError(null);
    setLoading(false);
    setPendingLoginData(null); // Clear pending data if user cancels
    setUploadProgress({ step: 0, total: 0 }); // Reset progress
  };

  // Job Description management functions
  const uploadJDText = async (jobTitle, companyName, jdContent) => {
    try {
      // Validate input before upload
      if (!jobTitle || !jobTitle.trim()) {
        throw new Error('Job title is required');
      }
      if (!jdContent || !jdContent.trim()) {
        throw new Error('Job description content cannot be empty');
      }
      if (jdContent.trim().length < 50) {
        throw new Error('Job description is too short. Please provide a more detailed description.');
      }

      // Use pending login data if available, otherwise use auth data
      const loginData = pendingLoginData || authData;
      if (!loginData.userIdentity) {
        throw new Error('User identity not found. Please log in again.');
      }
      const response = await apiService.uploadJDText(loginData.userIdentity, jobTitle, companyName, jdContent, loginData.apiKey);
      console.log('✅ JD uploaded successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ JD upload failed:', error);
      // Enhance error message for common issues
      if (error.message.includes('cannot be empty')) {
        throw new Error('Job description content is required. Please provide the job description details.');
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        throw new Error('Upload failed due to connection issues. Please check your internet and try again.');
      }
      throw error;
    }
  };

  const uploadJDFile = async (jobTitle, companyName, file) => {
    try {
      // Validate input before upload
      if (!jobTitle || !jobTitle.trim()) {
        throw new Error('Job title is required');
      }
      if (!file) {
        throw new Error('Please select a job description file');
      }
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are supported for job descriptions');
      }
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Job description file size must be less than 5MB');
      }

      // Use pending login data if available, otherwise use auth data
      const loginData = pendingLoginData || authData;
      if (!loginData.userIdentity) {
        throw new Error('User identity not found. Please log in again.');
      }
      const response = await apiService.uploadJDFile(loginData.userIdentity, jobTitle, companyName, file, loginData.apiKey);
      console.log('✅ JD file uploaded successfully:', response);
      return response;
    } catch (error) {
      console.error('❌ JD file upload failed:', error);
      // Enhance error message for common issues
      if (error.message.includes('PDF files are supported')) {
        throw new Error('Only PDF files are supported. Please convert your job description to PDF format.');
      } else if (error.message.includes('File size')) {
        throw new Error('Job description file is too large. Please reduce the file size to under 5MB.');
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        throw new Error('Upload failed due to connection issues. Please check your internet and try again.');
      }
      throw error;
    }
  };

  const handleJDUploadSuccess = async (uploadResult) => {
    console.log('📋 JD upload successful, retrying login...', uploadResult);
    setShowJDModal(false);
    setJDError(null);

    // Retry login after successful upload using pending login data
    const loginData = pendingLoginData || authData;

    try {
      setLoading(true);

      // Check if we need to upload a resume
      console.log('🔄 Checking if resume upload is required...');

      // Check if we have email/password (new auth) or old auth
      let response;
      if (loginData.email && loginData.password) {
        response = await apiService.login(loginData.email, loginData.password);

        // Update auth data with successful login
        setAuthData({
          isAuthenticated: true,
          userId: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          accessToken: response.accessToken,
          sessionId: response.sessionId,
          roomName: response.roomName || null,
          livekitToken: response.livekitToken || null,
          livekitUrl: response.livekitUrl || null,
          userIdentity: response.user.email,
          expiresIn: response.expiresIn || 0
        });

        localStorage.setItem('authData', JSON.stringify({
          userId: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          accessToken: response.accessToken,
          sessionId: response.sessionId
        }));
      } else {
        // Legacy auth support
        response = await apiService.login(loginData.userIdentity, loginData.appId, loginData.apiKey);

        setAuthData({
          isAuthenticated: true,
          sessionId: response.session_id,
          roomName: response.room_name,
          livekitToken: response.livekit_token,
          livekitUrl: response.livekit_url,
          userIdentity: response.user_identity,
          appId: loginData.appId,
          apiKey: loginData.apiKey,
          expiresIn: response.expires_in
        });
      }

      console.log('✅ Authentication successful after JD upload');

      // Reset upload progress on successful login
      setUploadProgress({ step: 0, total: 0 });

      // Auto-connect to voice if credentials are provided
      if (response.livekitUrl && response.livekitToken && response.roomName) {
        console.log('🔗 AuthContext: Auto-connecting to voice after JD upload...');
        try {
          await connectToVoiceWithData(response.livekitUrl, response.livekitToken, response.roomName);
        } catch (voiceError) {
          console.warn('⚠️ AuthContext: Auto voice connection failed:', voiceError);
        }
      }

    } catch (error) {
      // Intelligent flow: Check if the error is now about missing resume
      const errorMessage = error.message.toLowerCase();
      if (errorMessage.includes('resume')) {
        console.log('🔄 JD uploaded successfully, but resume is missing. Showing resume modal...');

        // Update authData to preserve user credentials for resume upload
        setAuthData(prev => ({
          ...prev,
          email: loginData.email,
          userIdentity: loginData.email || loginData.userIdentity
        }));

        setResumeError(`${error.message}\n\n✅ Job description uploaded successfully!\nStep 1 of 2: Now upload your resume to complete the setup.`);
        setShowResumeModal(true);
        setError(null);
        setUploadProgress({ step: 1, total: 2 });
        // Keep pending login data for resume upload
        return;
      } else {
        console.error('❌ Login retry failed after JD upload:', error);
        setError('Login failed after JD upload. Please try again.');
        setPendingLoginData(null);
      }
    } finally {
      setLoading(false);
      // Only clear pending data if login was successful or there's no resume requirement
      if (!showResumeModal) {
        setPendingLoginData(null);
      }
    }
  };

  const closeJDModal = () => {
    setShowJDModal(false);
    setJDError(null);
    setLoading(false);
    setPendingLoginData(null); // Clear pending data if user cancels
    setUploadProgress({ step: 0, total: 0 }); // Reset progress
  };

  // Load saved auth data and interview ID on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('authData');
    const savedInterviewId = localStorage.getItem('currentInterviewId');

    if (savedAuth) {
      try {
        const parsed = JSON.parse(savedAuth);
        console.log('✅ Restored auth data from localStorage');
        setAuthData(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error('❌ Failed to load saved auth data:', error);
        localStorage.removeItem('authData');
      }
    }

    // Check URL for interview ID (takes precedence over localStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const urlInterviewId = urlParams.get('interviewId');

    if (urlInterviewId) {
      console.log('✅ Found interview ID in URL:', urlInterviewId);
      setCurrentInterviewId(urlInterviewId);
      localStorage.setItem('currentInterviewId', urlInterviewId);
    } else if (savedInterviewId) {
      console.log('✅ Found interview ID in localStorage:', savedInterviewId);
      setCurrentInterviewId(savedInterviewId);

      // Sync URL with localStorage
      const newUrl = `${window.location.pathname}?interviewId=${savedInterviewId}`;
      window.history.replaceState({}, '', newUrl);
      console.log('✅ URL synced with interview ID from localStorage');
      console.log('✅ Current URL:', window.location.href);
    }
  }, []);

  useEffect(() => {
    if (authData.isAuthenticated) {
      const interval = setInterval(checkAuthStatus, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.isAuthenticated]);

  // Keep URL in sync with currentInterviewId at all times
  useEffect(() => {
    // Small delay to ensure state is stable
    const timeoutId = setTimeout(() => {
      console.log('🔍 URL Sync Effect - currentInterviewId:', currentInterviewId);
      console.log('🔍 Current URL:', window.location.href);
      console.log('🔍 Voice Connected:', voiceConnected);
      console.log('🔍 Is Authenticated:', authData.isAuthenticated);

      const urlParams = new URLSearchParams(window.location.search);
      const urlInterviewId = urlParams.get('interviewId');

      if (currentInterviewId && urlInterviewId !== currentInterviewId) {
        // Interview ID exists but URL is out of sync - update it
        const url = new URL(window.location.href);
        url.searchParams.set('interviewId', currentInterviewId);
        window.history.replaceState({}, '', url.toString());
        console.log('🔗 URL synced with current interview ID:', currentInterviewId);
        console.log('🔗 New URL:', window.location.href);
      } else if (!currentInterviewId && urlInterviewId) {
        // No interview ID but URL has one - only clear if we're logged out (not just voice disconnected)
        // Don't clear if user is authenticated, as they might be in the process of starting/resuming
        if (!authData.isAuthenticated) {
          window.history.replaceState({}, '', window.location.pathname);
          console.log('🧹 URL cleared (user logged out)');
          console.log('🧹 New URL:', window.location.href);
        } else {
          console.log('⏸️ Keeping URL with interviewId (user authenticated, may be starting/resuming)');
        }
      } else if (currentInterviewId && urlInterviewId === currentInterviewId) {
        console.log('✅ URL already in sync:', currentInterviewId);
      }
    }, 100); // Small delay to let state settle

    return () => clearTimeout(timeoutId);
  }, [currentInterviewId, authData.isAuthenticated, voiceConnected]);

  // Check for resumable interview on page load/refresh
  useEffect(() => {
    const checkForResumableInterview = async () => {
      if (!authData.isAuthenticated || !authData.accessToken || !authData.sessionId) {
        console.log('⏸️ Skipping resume check: Not authenticated');
        return;
      }

      if (!currentInterviewId) {
        console.log('⏸️ Skipping resume check: No interview ID');
        return;
      }

      // If we already have LiveKit connection, don't try to resume
      if (voiceConnected) {
        console.log('⏸️ Skipping resume check: Already connected');
        return;
      }

      try {
        console.log('🔍 Checking for resumable interview:', currentInterviewId);

        // Get interview details
        const interviewDetails = await apiService.getInterview(
          currentInterviewId,
          authData.accessToken,
          authData.sessionId
        );

        const interview = interviewDetails.data || interviewDetails;

        // Check if interview is resumable and active
        if (interview.resumable && interview.status === 'active') {
          console.log('✅ Found resumable active interview, auto-resuming...');
          // Note: loadConversationHistory will be called from InterviewApp
          // which has access to both contexts
          await resumeInterview(currentInterviewId, null);
        } else {
          // Only clear if interview is completed or cancelled (not just starting)
          // Don't clear if status is 'pending' or 'starting' as interview might be in progress
          if (interview.status === 'completed' || interview.status === 'cancelled' || interview.status === 'failed') {
            console.log('ℹ️ Interview ended or cancelled, clearing saved ID');
            localStorage.removeItem('currentInterviewId');
            setCurrentInterviewId(null);

            // Clear URL parameter
            const url = new URL(window.location.href);
            url.searchParams.delete('interviewId');
            window.history.replaceState({}, '', url.toString());
          } else {
            console.log('ℹ️ Interview status:', interview.status, '- keeping interview ID in URL');
          }
        }
      } catch (error) {
        console.error('❌ Error checking for resumable interview:', error);
        // Only clear if it's an auth error (401/403) - otherwise might be temporary network issue
        const isAuthError = error.message && (error.message.includes('401') || error.message.includes('403') || error.message.includes('unauthorized'));
        if (isAuthError) {
          console.log('🔐 Auth error detected, clearing interview ID');
          localStorage.removeItem('currentInterviewId');
          setCurrentInterviewId(null);

          // Clear URL parameter
          const url = new URL(window.location.href);
          url.searchParams.delete('interviewId');
          window.history.replaceState({}, '', url.toString());
        } else {
          console.log('⚠️ Non-auth error, keeping interview ID in URL (might be temporary)');
        }
      }
    };

    // Only run if we have all required data and are not already trying to resume
    if (authData.isAuthenticated && currentInterviewId && !voiceConnected) {
      // Small delay to ensure everything is loaded
      const timeoutId = setTimeout(checkForResumableInterview, 500);
      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData.isAuthenticated, authData.accessToken, authData.sessionId, currentInterviewId]);

  const value = {
    ...authData,
    loading,
    error,
    voiceConnected,
    connectionState,
    participants,
    showResumeModal,
    resumeError,
    showJDModal,
    jdError,
    uploadProgress,
    currentInterviewId,
    setCurrentInterviewId,
    login,
    logout,
    checkAuthStatus,
    startInterviewWithSelection,
    resumeInterview,
    connectToVoice,
    connectToVoiceWithData,
    disconnectFromVoice,
    enableMicrophone,
    disableMicrophone,
    uploadResume,
    handleResumeUploadSuccess,
    closeResumeModal,
    uploadJDText,
    uploadJDFile,
    handleJDUploadSuccess,
    closeJDModal
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};